import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { api, ApiError, type AgentWithCounts, type TestConnectionResponse, type UsageResponse, type WorkspaceResponse } from '@/lib/api';
import { Sidebar, Toast, useConfirm, type NavKey } from '@/components';
import { ForgotPassword, ResetPassword, SignIn } from '@/features/auth';
import { AgentsGallery, AgentBuilder, draftFromAgent, emptyDraft, type AgentDraft } from '@/features/agents';
import { ChatWorkspace } from '@/features/chat';
import { KnowledgeLibrary } from '@/features/knowledge';
import { ConnectionScreen } from '@/features/connection';
import { UsageScreen } from '@/features/usage';
import { SettingsScreen } from '@/features/settings';
import {
  MAX_CONVERT_BYTES,
  type AgentScope,
  type ChatMessage,
  type KnowledgeSource,
  type WorkspaceSettings,
  type WorkspaceUser,
} from '@shared/types';
import './App.scss';

type View = NavKey | 'builder' | 'chat';

interface ToastState {
  message: string;
  tone: 'success' | 'warning' | 'danger';
}

export default function App() {
  const location = useLocation();
  const [session, setSession] = useState<Session | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [customerName, setCustomerName] = useState('Locate2u');

  // A recovery or invite link signs the user in, so `session` alone would drop
  // them into the workspace with no way to set a password. This keeps the reset
  // screen up until they have actually chosen one. Seeded from the URL because
  // Supabase may consume the hash before our listener is attached — and for an
  // invite the seed is the only signal there is, since Supabase fires a plain
  // SIGNED_IN for it rather than a distinct event.
  const [passwordFlow, setPasswordFlow] = useState<'recovery' | 'invite' | null>(() => {
    const hash = window.location.hash;
    if (hash.includes('type=recovery')) return 'recovery';
    if (hash.includes('type=invite')) return 'invite';
    return null;
  });

  const [view, setView] = useState<View>('gallery');
  const [agents, setAgents] = useState<AgentWithCounts[]>([]);
  const [sources, setSources] = useState<KnowledgeSource[]>([]);
  const [workspace, setWorkspace] = useState<WorkspaceResponse | null>(null);
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  const [role, setRole] = useState<'member' | 'admin'>('member');
  const [users, setUsers] = useState<WorkspaceUser[]>([]);

  const [activeAgentId, setActiveAgentId] = useState<string | null>(null);
  const [draft, setDraft] = useState<AgentDraft | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);

  const [loadingChat, setLoadingChat] = useState(false);
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState<TestConnectionResponse | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);

  const toastTimer = useRef<number | undefined>(undefined);
  const confirm = useConfirm();

  const notify = useCallback((message: string, tone: ToastState['tone'] = 'success') => {
    window.clearTimeout(toastTimer.current);
    setToast({ message, tone });
    toastTimer.current = window.setTimeout(() => setToast(null), 2600);
  }, []);

  /** Turns any thrown error into a toast without losing the API's message. */
  const fail = useCallback(
    (err: unknown, fallback: string) => {
      const message = err instanceof ApiError ? err.message : fallback;
      notify(message, 'danger');
      if (!(err instanceof ApiError)) console.error(err);
    },
    [notify],
  );

  // --- Auth ---------------------------------------------------------------
  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setAuthReady(true);
    });
    const { data: subscription } = supabase.auth.onAuthStateChange((event, next) => {
      if (event === 'PASSWORD_RECOVERY') setPasswordFlow('recovery');
      setSession(next);
    });
    return () => subscription.subscription.unsubscribe();
  }, []);

  // Customer name for the sign-in screen, before any token exists.
  useEffect(() => {
    void fetch('/api/public-config')
      .then((res) => (res.ok ? (res.json() as Promise<{ customerName?: string }>) : null))
      .then((body) => {
        if (body?.customerName) setCustomerName(body.customerName);
      })
      .catch(() => {
        /* branding is cosmetic; the default stands */
      });
  }, []);

  // --- Initial load -------------------------------------------------------
  const refreshAll = useCallback(async () => {
    try {
      const [agentsRes, sourcesRes, workspaceRes, usageRes, meRes] = await Promise.all([
        api.agents(),
        api.knowledge(),
        api.workspace(),
        api.usage(),
        api.me(),
      ]);
      setAgents(agentsRes.agents);
      setSources(sourcesRes.sources);
      setWorkspace(workspaceRes);
      setUsage(usageRes);
      setRole(meRes.user.role);
      setCustomerName(workspaceRes.customer.name);

      // Members are refused this list by design, so it is fetched apart from
      // the rest — and its own failure must not read as the workspace failing.
      if (meRes.user.role === 'admin') {
        try {
          setUsers((await api.users()).users);
        } catch (err) {
          fail(err, 'Could not load the user list.');
        }
      }
    } catch (err) {
      fail(err, 'Could not load your workspace.');
    }
  }, [fail]);

  useEffect(() => {
    if (session) void refreshAll();
  }, [session, refreshAll]);

  // --- Derived ------------------------------------------------------------
  const activeAgent = useMemo(
    () => agents.find((a) => a.id === activeAgentId) ?? null,
    [agents, activeAgentId],
  );

  /** Sources visible to the agent being edited: explicit assignment or "all". */
  const draftSourceCount = useMemo(() => {
    if (!draft) return 0;
    return sources.filter(
      (s) => s.agentIds.length === 0 || (draft.id !== null && s.agentIds.includes(draft.id)),
    ).length;
  }, [draft, sources]);

  // --- Navigation ---------------------------------------------------------
  function navigate(key: NavKey) {
    setView(key);
    if (key === 'gallery') setDraft(null);
  }

  async function openChat(agentId: string) {
    setActiveAgentId(agentId);
    setView('chat');
    setMessages([]);
    setLoadingChat(true);
    try {
      const res = await api.messages(agentId);
      setMessages(res.messages);
      setConversationId(res.conversationId);
    } catch (err) {
      fail(err, 'Could not load that conversation.');
    } finally {
      setLoadingChat(false);
    }
  }

  function configureAgent(agentId: string) {
    const agent = agents.find((a) => a.id === agentId);
    if (!agent) return;
    setDraft(draftFromAgent(agent));
    setView('builder');
  }

  function newAgent() {
    setDraft(emptyDraft());
    setView('builder');
  }

  // --- Actions ------------------------------------------------------------
  async function sendMessage(text: string) {
    if (!activeAgent) return;
    const optimistic: ChatMessage = {
      id: `pending-${Date.now()}`,
      role: 'user',
      content: text,
      queriedResources: [],
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setSending(true);
    try {
      const res = await api.send(activeAgent.id, text, conversationId);
      setConversationId(res.conversationId);
      setMessages((prev) => [...prev, res.message]);
      if (res.budget.status === 'warning') {
        notify(`Heads up — ${res.budget.pct.toFixed(0)}% of your token limit used`, 'warning');
      }
      // Budget and ledger moved; refresh them without blocking the reply.
      void api.workspace().then(setWorkspace).catch(() => undefined);
      void api.usage().then(setUsage).catch(() => undefined);
    } catch (err) {
      // Roll the optimistic message back so the thread matches the server.
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      fail(err, 'Could not send that message.');
    } finally {
      setSending(false);
    }
  }

  async function saveDraft() {
    if (!draft) return;
    if (!draft.name.trim()) {
      notify('Give the agent a name first', 'warning');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: draft.name,
        purpose: draft.purpose,
        colour: draft.colour,
        rules: draft.rules,
        useKnowledge: draft.useKnowledge,
        useApi: draft.useApi,
        scopes: draft.scopes,
        model: draft.model,
      };
      if (draft.id) await api.updateAgent(draft.id, payload);
      else await api.createAgent(payload);
      const res = await api.agents();
      setAgents(res.agents);
      setDraft(null);
      setView('gallery');
      notify('Agent saved');
    } catch (err) {
      fail(err, 'Could not save that agent.');
    } finally {
      setBusy(false);
    }
  }

  async function deleteDraft() {
    if (!draft?.id) return;
    const ok = await confirm({
      title: `Delete “${draft.name || 'this agent'}”?`,
      body: 'Its conversations and usage history are kept, so past spend stays auditable. This cannot be undone.',
      confirmLabel: 'Delete agent',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.deleteAgent(draft.id);
      setAgents((prev) => prev.filter((a) => a.id !== draft.id));
      if (activeAgentId === draft.id) setActiveAgentId(null);
      setDraft(null);
      setView('gallery');
      notify('Agent deleted');
    } catch (err) {
      fail(err, 'Could not delete that agent.');
    } finally {
      setBusy(false);
    }
  }

  async function uploadFiles(files: File[], agentIds: string[]) {
    // Sent from here rather than discovered at the far end: a PDF this large
    // cannot be converted, and the browser already knows its size, so there is
    // no reason to spend minutes uploading it first. The Worker checks too.
    const oversize = files.filter(
      (file) => /\.pdf$/i.test(file.name) && file.size > MAX_CONVERT_BYTES,
    );
    const sendable = files.filter((file) => !oversize.includes(file));

    if (sendable.length === 0) {
      const limit = MAX_CONVERT_BYTES / 1024 / 1024;
      notify(
        oversize.length === 1
          ? `${oversize[0].name} is too large to read (limit ${limit} MB). Split it into smaller parts.`
          : `${oversize.length} PDFs are too large to read (limit ${limit} MB each).`,
        'warning',
      );
      return;
    }

    setBusy(true);
    let added = 0;
    const failures: string[] = [];
    for (const file of sendable) {
      try {
        await api.uploadSource(file, agentIds);
        added += 1;
      } catch (err) {
        failures.push(file.name);
        if (!(err instanceof ApiError)) console.error(err);
      }
    }
    await Promise.all([
      api.knowledge().then((r) => setSources(r.sources)),
      api.agents().then((r) => setAgents(r.agents)),
    ]).catch(() => undefined);
    setBusy(false);

    const skipped = oversize.map((file) => file.name);
    const allFailed = [...failures, ...skipped];
    if (allFailed.length > 0) {
      notify(`${added} added, ${allFailed.length} failed: ${allFailed.join(', ')}`, 'warning');
    } else {
      notify(added === 1 ? 'Source added' : `${added} sources added`);
    }
  }

  async function addTextSource(name: string, content: string, agentIds: string[]) {
    setBusy(true);
    try {
      const res = await api.addTextSource({ name, content, agentIds });
      const [sourcesRes, agentsRes] = await Promise.all([api.knowledge(), api.agents()]);
      setSources(sourcesRes.sources);
      setAgents(agentsRes.agents);
      notify(
        res.source.status === 'ready'
          // Vectorize accepts a write before it can answer for it, so the claim
          // is that the source is stored, not that it is already searchable.
          ? `Source added and indexed (${res.chunks} chunks) — searchable in a moment`
          : 'Source added, but indexing failed',
        res.source.status === 'ready' ? 'success' : 'warning',
      );
    } catch (err) {
      fail(err, 'Could not add that source.');
    } finally {
      setBusy(false);
    }
  }

  /** Reads a source's text for the library's inline editor. null = it failed. */
  async function loadSourceText(sourceId: string): Promise<string | null> {
    try {
      const res = await api.getSource(sourceId);
      return res.content;
    } catch (err) {
      fail(err, 'Could not open that source.');
      return null;
    }
  }

  async function editSource(sourceId: string, name: string, content: string): Promise<boolean> {
    try {
      const res = await api.updateSource(sourceId, { name, content });
      const sourcesRes = await api.knowledge();
      setSources(sourcesRes.sources);
      if (res.source.status === 'ready') {
        notify(
          res.chunks === undefined
            ? 'Source updated'
            : `Source updated and re-indexed (${res.chunks} chunks) — searchable in a moment`,
        );
      } else {
        // A failed source is excluded from retrieval, so this cannot be quiet.
        notify('Saved, but indexing failed — agents cannot use this source until it indexes', 'warning');
      }
      return true;
    } catch (err) {
      fail(err, 'Could not save that source.');
      return false;
    }
  }

  async function assignSource(sourceId: string, agentIds: string[]) {
    // Optimistic: assignment is a cheap toggle and should feel instant.
    const previous = sources;
    setSources((prev) => prev.map((s) => (s.id === sourceId ? { ...s, agentIds } : s)));
    try {
      await api.assignSource(sourceId, agentIds);
      const res = await api.agents();
      setAgents(res.agents);
    } catch (err) {
      setSources(previous);
      fail(err, 'Could not change that assignment.');
    }
  }

  async function deleteSource(sourceId: string) {
    const previous = sources;
    setSources((prev) => prev.filter((s) => s.id !== sourceId));
    try {
      await api.deleteSource(sourceId);
      const res = await api.agents();
      setAgents(res.agents);
      notify('Source removed');
    } catch (err) {
      setSources(previous);
      fail(err, 'Could not remove that source.');
    }
  }

  async function patchWorkspace(patch: Partial<WorkspaceSettings> & { l2uApiKey?: string }) {
    setBusy(true);
    try {
      const res = await api.updateWorkspace(patch);
      setWorkspace((prev) => (prev ? { ...prev, settings: res.settings, budget: res.budget } : prev));
      notify('Settings saved');
    } catch (err) {
      fail(err, 'Could not save settings.');
    } finally {
      setBusy(false);
    }
  }

  /** Returns whether the invite went out, so the form knows to clear itself. */
  async function inviteUser(email: string, inviteRole: 'member' | 'admin'): Promise<boolean> {
    try {
      const res = await api.inviteUser({ email, role: inviteRole });
      setUsers((await api.users()).users);
      // The account exists either way; the warning only covers the role.
      if (res.warning) notify(res.warning, 'warning');
      else notify(`Invite sent to ${email}`);
      return true;
    } catch (err) {
      fail(err, 'Could not send that invite.');
      return false;
    }
  }

  async function changeUserRole(userId: string, nextRole: 'member' | 'admin') {
    // Optimistic: the select should settle immediately, like the agent toggles
    // on a knowledge source.
    const previous = users;
    setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role: nextRole } : u)));
    try {
      await api.updateUserRole(userId, nextRole);
      // Demoting yourself takes the Users tab away with it, so the role this
      // screen is gated on has to be re-read rather than assumed unchanged.
      if (userId === session?.user.id) {
        const me = await api.me();
        setRole(me.user.role);
      }
      notify(nextRole === 'admin' ? 'Now an administrator' : 'Now a member');
    } catch (err) {
      setUsers(previous);
      fail(err, 'Could not change that role.');
    }
  }

  async function removeUser(userId: string) {
    const previous = users;
    const gone = users.find((u) => u.id === userId);
    setUsers((prev) => prev.filter((u) => u.id !== userId));
    try {
      await api.removeUser(userId);
      notify(gone ? `Removed ${gone.email}` : 'User removed');
    } catch (err) {
      setUsers(previous);
      fail(err, 'Could not remove that user.');
    }
  }

  async function runConnectionTest() {
    setBusy(true);
    try {
      setTestResult(await api.testConnection());
    } catch (err) {
      fail(err, 'The test query failed.');
    } finally {
      setBusy(false);
    }
  }

  // --- Render -------------------------------------------------------------
  if (!authReady) {
    return <div className="app-boot">Loading…</div>;
  }

  // Signed out, or mid-recovery/invite: the /auth/* shell owns the screen.
  if (!session || passwordFlow) {
    return (
      <Routes>
        <Route path="/auth/sign-in" element={<SignIn customerName={customerName} />} />
        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
        <Route
          path="/auth/reset-password"
          element={
            <ResetPassword
              ready={passwordFlow !== null}
              mode={passwordFlow ?? 'recovery'}
              onDone={() => setPasswordFlow(null)}
            />
          }
        />
        <Route path="*" element={<Navigate to="/auth/sign-in" replace />} />
      </Routes>
    );
  }

  // Signed in but still sitting on an /auth/* URL: bounce into the workspace.
  if (location.pathname.startsWith('/auth/')) {
    return <Navigate to="/" replace />;
  }

  if (!workspace || !usage) {
    return <div className="app-boot">Loading your workspace…</div>;
  }

  const { settings, budget, models, l2uApiMode } = workspace;
  const isAdmin = role === 'admin';

  return (
    <div className="app">
      <Sidebar
        customerName={customerName}
        agents={agents}
        activeView={view}
        activeAgentId={activeAgentId}
        connected={settings.l2uConnected}
        onNavigate={navigate}
        onSelectAgent={openChat}
        onNewAgent={newAgent}
        onSignOut={() => void supabase.auth.signOut()}
      />

      <main className="app__content">
        {view === 'gallery' && (
          <AgentsGallery
            agents={agents}
            connected={settings.l2uConnected}
            onOpenChat={openChat}
            onConfigure={configureAgent}
            onNewAgent={newAgent}
          />
        )}

        {view === 'builder' && draft && (
          <AgentBuilder
            draft={draft}
            models={models}
            defaultModel={settings.defaultModel}
            sourceCount={draftSourceCount}
            saving={busy}
            onChange={setDraft}
            onSave={saveDraft}
            onDelete={deleteDraft}
            onBack={() => navigate('gallery')}
          />
        )}

        {view === 'chat' && activeAgent && (
          <ChatWorkspace
            agent={activeAgent}
            messages={messages}
            loading={loadingChat}
            sending={sending}
            connected={settings.l2uConnected}
            onSend={sendMessage}
            onConfigure={() => configureAgent(activeAgent.id)}
            onBack={() => navigate('gallery')}
          />
        )}

        {view === 'knowledge' && (
          <KnowledgeLibrary
            sources={sources}
            agents={agents}
            busy={busy}
            onUpload={uploadFiles}
            onAddText={addTextSource}
            onAssign={assignSource}
            onDelete={deleteSource}
            onLoadSourceText={loadSourceText}
            onEditSource={editSource}
          />
        )}

        {view === 'connection' && (
          <ConnectionScreen
            settings={settings}
            apiMode={l2uApiMode}
            canEdit={isAdmin}
            busy={busy}
            testResult={testResult}
            onConnect={(key) => void patchWorkspace({ l2uApiKey: key })}
            onDisconnect={() => void patchWorkspace({ l2uConnected: false })}
            onEnvironmentChange={(env) => void patchWorkspace({ l2uEnvironment: env })}
            onScopeToggle={(scope: AgentScope, enabled) =>
              void patchWorkspace({ l2uScopes: { ...settings.l2uScopes, [scope]: enabled } })
            }
            onTest={runConnectionTest}
          />
        )}

        {view === 'usage' && <UsageScreen usage={usage} agents={agents} />}

        {view === 'settings' && (
          <SettingsScreen
            settings={settings}
            budget={budget}
            models={models}
            usage={usage}
            canEdit={isAdmin}
            busy={busy}
            onSave={(patch) => void patchWorkspace(patch)}
            users={users}
            currentUserId={session.user.id}
            onInvite={inviteUser}
            onChangeRole={changeUserRole}
            onRemove={removeUser}
          />
        )}
      </main>

      {toast && <Toast message={toast.message} tone={toast.tone} />}
    </div>
  );
}
